// @ts-check
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import ButtonLinkWidget from '@glowforge/gf-styled/src/contentful-widgets/button-link';

import {
  PillButtonWrapper,
  PillButton,
  Picture,
  ButtonWrapper,
  ImageFlexContainer,
  SingleImageWrapper,
  MultiImageWrapper,
  MultiPicture,
  SwipeText,
} from './explore-materials-secondary.styles';

import { Title, Text, Wrapper } from './explore-materials.styles';

/**
 * @typedef {object} Materials
 * @property {string} id Unique id for image
 * @property {string} title Title of the material
 * @property {object} images images array (see gf-styled model)
 */

/**
 * @typedef {object} CtaProps
 * @property {string} id
 * @property {string} internalName
 * @property {string} size
 * @property {string} text
 * @property {string} type
 * @property {string} url
 */

/**
 * @typedef {object} Props
 * @property {Materials[]} materials - array of material objects
 * @property {string} title
 * @property {string} text
 * @property {CtaProps} ctaProps
 */

const ExploreMaterialsSecondary = ({
  title,
  text,
  materials = [],
  ctaButton,
  attributes = {},
  isBuilder,
  BuilderImage,
}) => {
  const [selectedMaterial, setSelectedMaterial] = useState(
    materials.length > 0 && materials[0]
  );

  const images = selectedMaterial?.images;
  const firstImage = images[0]?.image;
  const middleImages = images?.slice(1, images?.length - 1);
  const lastImage = images[images.length - 1]?.image;

  return (
    <Wrapper variant='full' isBuilder={isBuilder} {...attributes}>
      {title ? <Title>{title}</Title> : null}
      {text ? <Text>{text}</Text> : null}
      <PillButtonWrapper isBuilder={isBuilder}>
        {materials.map((material) => (
          <PillButton
            key={material.id}
            active={material.id === selectedMaterial.id}
            onClick={() => setSelectedMaterial(material)}>
            {isBuilder ? material.id : material.title}
          </PillButton>
        ))}
      </PillButtonWrapper>
      <SwipeText>&lt; Swipe to select more materials &gt;</SwipeText>
      <ImageFlexContainer isBuilder={isBuilder}>
        {isBuilder && (
          <>
            <SingleImageWrapper>
              <Picture
                index={0}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                key={`${selectedMaterial?.id}_${firstImage}_0`}>
                <BuilderImage
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'cover',
                  }}
                  image={firstImage}
                  height='100%'
                  lazy={false}
                />
              </Picture>
            </SingleImageWrapper>
            {images?.length >= 3 && (
              <MultiImageWrapper isBuilder={isBuilder}>
                {middleImages.map((image, index) => {
                  const key = `${selectedMaterial?.id}_${image.image}_${
                    index + 1
                  }`;
                  return (
                    <MultiPicture
                      key={key}
                      index={index}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}>
                      <BuilderImage
                        style={{
                          height: '100%',
                          width: '100%',
                          objectFit: 'cover',
                        }}
                        image={image?.image}
                        height={389}
                        lazy={false}
                      />
                    </MultiPicture>
                  );
                })}
              </MultiImageWrapper>
            )}

            <SingleImageWrapper>
              <Picture
                key={`${selectedMaterial?.id}_${lastImage}_${
                  images.length + 1
                }`}
                index={images.length - 1}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}>
                <BuilderImage
                  style={{
                    height: '100%',
                    width: '100%',
                    objectFit: 'cover',
                  }}
                  image={lastImage}
                  height={389}
                  lazy={false}
                />
              </Picture>
            </SingleImageWrapper>
          </>
        )}
      </ImageFlexContainer>
      <ButtonWrapper>
        <ButtonLinkWidget model={ctaButton} />
      </ButtonWrapper>
    </Wrapper>
  );
};

ExploreMaterialsSecondary.propTypes = {
  attributes: PropTypes.shape({}),
  BuilderImage: PropTypes.element,
  ctaButton: PropTypes.shape({}),
  isBuilder: PropTypes.bool,
  materials: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      images: PropTypes.arrayOf(
        // There's more here, but it's ridiculous to repeat them everywhere
        PropTypes.shape({
          id: PropTypes.string,
          title: PropTypes.string,
          default: PropTypes.shape({
            title: PropTypes.string,
            description: PropTypes.string,
            file: PropTypes.shape({
              url: PropTypes.string,
            }),
          }),
        })
      ),
    })
  ),
  text: PropTypes.string,
  title: PropTypes.string,
};

ExploreMaterialsSecondary.defaultProps = {
  attributes: {},
  BuilderImage: null,
  ctaButton: null,
  isBuilder: false,
  materials: [],
  text: null,
  title: null,
};

export default ExploreMaterialsSecondary;
